import { useLayoutEffect, useRef } from 'react';

const STYLE_ID = '__custom-scroll-lock-style';

const LOCK_SOURCES_ATTR = 'data-scroll-lock-sources';

const STYLE_CONTENT = `
body[data-scroll-locked="1"] {
  overflow: hidden !important;
  overscroll-behavior: contain;
  position: relative !important;
  margin-right: var(--removed-body-scroll-bar-size, 15px) !important;
}
`;

// Generate unique ID for each scroll lock instance
const generateLockId = (): string => {
  return `lock-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get current lock sources from body attribute
const getLockSources = (): string[] => {
  const body = document.body;

  const sourcesAttr = body.getAttribute(LOCK_SOURCES_ATTR);

  return sourcesAttr ? sourcesAttr.split(',').filter(Boolean) : [];
};

// Set lock sources to body attribute
const setLockSources = (sources: string[]): void => {
  const body = document.body;

  if (sources.length > 0) {
    body.setAttribute(LOCK_SOURCES_ATTR, sources.join(','));
  } else {
    body.removeAttribute(LOCK_SOURCES_ATTR);
  }
};

// Add a lock source
const addLockSource = (lockId: string): void => {
  const sources = getLockSources();

  if (!sources.includes(lockId)) {
    sources.push(lockId);

    setLockSources(sources);
  }
};

// Remove a lock source
const removeLockSource = (lockId: string): void => {
  const sources = getLockSources();

  const filteredSources = sources.filter((id) => id !== lockId);

  setLockSources(filteredSources);
};

// Check if scroll should be locked (any sources exist)
const shouldLockScroll = (): boolean => {
  return getLockSources().length > 0;
};

export function useScrollLock(locked: boolean) {
  const lockIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const body = document.body;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    const ensureStyle = () => {
      let styleTag = document.getElementById(
        STYLE_ID,
      ) as HTMLStyleElement | null;

      if (!styleTag) {
        styleTag = document.createElement('style');

        styleTag.id = STYLE_ID;

        document.head.appendChild(styleTag);
      }

      if (styleTag.innerHTML !== STYLE_CONTENT) {
        styleTag.innerHTML = STYLE_CONTENT;
      }
    };

    if (locked) {
      // Generate a unique ID for this lock instance if we don't have one
      if (!lockIdRef.current) {
        lockIdRef.current = generateLockId();
      }

      // Add this lock source
      addLockSource(lockIdRef.current);

      // Apply scroll lock styles
      body.setAttribute('data-scroll-locked', '1');

      body.style.setProperty(
        '--removed-body-scroll-bar-size',
        `${scrollbarWidth}px`,
      );

      ensureStyle();
    } else {
      // Remove this lock source if it exists
      if (lockIdRef.current) {
        removeLockSource(lockIdRef.current);

        lockIdRef.current = null;
      }

      // Only remove scroll lock if no other sources exist
      if (!shouldLockScroll()) {
        body.removeAttribute('data-scroll-locked');

        body.style.removeProperty('--removed-body-scroll-bar-size');
      }
    }

    return () => {
      // Cleanup: remove this lock source when component unmounts
      if (lockIdRef.current) {
        removeLockSource(lockIdRef.current);

        // Only remove scroll lock if no other sources exist
        if (!shouldLockScroll()) {
          body.removeAttribute('data-scroll-locked');

          body.style.removeProperty('--removed-body-scroll-bar-size');
        }

        lockIdRef.current = null;
      }
    };
  }, [locked]);
}
